import { FileResponse } from '@agent/agent-interface/contact-view/chat/model/chat-attachment-message';
import { ChatInitData } from '@agent/agent-interface/contact-view/chat/model/chat-init-data';
import { ChatTab } from '@agent/agent-interface/contact-view/chat/model/chat-tab';
import { OwiInstantMessage } from '@agent/agent-interface/contact-view/chat/model/owi-instant-message';
import { ScreenSharingInfo, ScreenSharingStatus } from '@agent/agent-interface/contact-view/chat/model/screen-sharing-info';
import { SystemMessageType } from '@agent/agent-interface/contact-view/chat/model/system-message-type';
import { AgentImageService } from '@agent/agent-interface/contact-view/chat/services/agent-image.service';
import { ChatContactBuilderService } from '@agent/agent-interface/contact-view/chat/services/chat-contact-builder.service';
import { ChatStatsService } from '@agent/agent-interface/contact-view/chat/services/chat-stats.service';
import { ScreenSharingStatusService } from '@agent/agent-interface/contact-view/chat/services/screen-sharing-status.service';
import { Injectable, Injector } from '@angular/core';
import { NG1_KNOWLEDGE_BASE } from '@commons/core/core.provider.ajs';
import { ChannelUtil } from '@commons/util/helper/channel';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Client, messageCallbackType, StompHeaders, StompSubscription } from '@stomp/stompjs';
import { BehaviorSubject, filter, of, Subject, switchMap, takeUntil, withLatestFrom } from 'rxjs';
import SockJS from 'sockjs-client';
import { ChatActionsService } from '@agent/agent-interface/contact-view/chat/services/chat-actions.service';

import {
  Acceptation,
  AgentConfig,
  AgentConfigFacade,
  AgentSkillFacade,
  AppConf,
  Booking,
  BookingAction,
  BookingFacade,
  BookingStatus,
  ChannelType,
  CSR_FACADE_PREFIX,
  Customer,
  CustomerAction,
  CustomerFacade,
  CustomerService,
  HttpService,
  isAgentReady,
  isNotNullOrUndefined,
  Logger,
  notNullOrUndefined,
  OpeningChannel,
  Profile,
  ProfileFacade,
  ProfileService,
  ToastClass,
  ToasterFacade,
  TranslateService,
  waitToLoad,
} from '@contact/upgrade';

import { ChatMessage } from '../model/chat-message';
import { ChatModel } from '../model/chat-model';
import { ChatSystemMessage } from '../model/chat-system-message';
import { MessageType } from '../model/message-type';
import { ChatTabService } from './chat-tab.service';
import { ChatTimerService } from './chat-timer.service';

const DESTINATION_PREFIX = '/queue'; // "/topic" or "/queue"
const INIT_DATA_MAPPING = '/chat/initData';
const MESSAGE_MAPPING = DESTINATION_PREFIX + '/chat.message';
const SYSTEM_MAPPING = DESTINATION_PREFIX + '/system.message';
const ERROR_MESSAGE_MAPPING = DESTINATION_PREFIX + '/errors';

export const MESSAGE_ENDPOINT = '/chat/chat.message';
export const SYSTEM_MESSAGE_ENDPOINT = '/chat/system.message';

export const TYPE_PENDING_DEBOUNCE_TIME = 2000;
export const TYPE_END_DEBOUNCE_TIME = 5000;

@Injectable({
  providedIn: 'root',
})
export class ChatSocketService {
  private readonly _wssUrlChunk = '/wss';

  private _chatUrl: string | undefined = undefined;
  private _headers: StompHeaders;
  private _stompClient: Client;
  private _chatMap = new Map<string, ChatModel>();

  private _connected = false;
  private _subscriptions: (StompSubscription | undefined)[] = [];

  private _chatList: Array<ChatModel> = [];

  private _profile: Profile;
  private _agentConfig: AgentConfig;
  private _acceptation: Acceptation;

  private _selectedContact: Booking | undefined;

  private readonly _chatInQualificationCount$ = new BehaviorSubject<number>(0);
  readonly chatInQualificationCount$ = this._chatInQualificationCount$.asObservable();

  private readonly _chatHavePostTreatmentDurationExceeded$ = new BehaviorSubject<boolean>(false);
  readonly chatHavePostTreatmentDurationExceeded$ = this._chatHavePostTreatmentDurationExceeded$.asObservable();

  private readonly _chatNewMessageCount$ = new BehaviorSubject<number>(0);
  readonly chatNewMessageCount$ = this._chatNewMessageCount$.asObservable();

  private readonly _destroy$ = new Subject<void>();
  readonly destroy$ = this._destroy$.asObservable();

  private isWelcomeMessageActive;
  private welcomeMessage;
  private messageDuringTransfer;

  websocketLost = false; // used in case of websocket lost / restart of chat server

  constructor(
    private readonly chatActions: ChatActionsService,
    private readonly logger: Logger,
    private readonly chatTabService: ChatTabService,
    private readonly appConf: AppConf,
    private readonly http: HttpService,
    private readonly toasterFacade: ToasterFacade,
    private readonly profileFacade: ProfileFacade,
    private readonly agentConfigFacade: AgentConfigFacade,
    private readonly translateService: TranslateService,
    private readonly chatTimerService: ChatTimerService,
    private readonly customerService: CustomerService,
    private readonly chatStatsService: ChatStatsService,
    private readonly screenSharingStatusService: ScreenSharingStatusService,
    private readonly store$: Store,
    private readonly agentSkillFacade: AgentSkillFacade,
    private readonly contactFacade: BookingFacade,
    private readonly injector: Injector,
    private readonly profileService: ProfileService,
    private readonly chatContactBuilderService: ChatContactBuilderService,
    private readonly agentImageService: AgentImageService,
    private readonly customerFacade: CustomerFacade,
    private readonly actions$: Actions
  ) {}

  init() {
    this.appConf.pipe(takeUntil(this.destroy$)).subscribe(({ chat: chatConf }) => {
      this.isWelcomeMessageActive = chatConf?.chatWelcomeMessage?.isWelcomeMessageActive;
      this.welcomeMessage = chatConf?.chatWelcomeMessage?.message;
      this.messageDuringTransfer = chatConf?.chatWelcomeMessage?.displayDuringTransfer;
      if (!chatConf?.chatBaseUrl) {
        throw new Error('chatBaseUrl is undefined');
      }
      this._chatUrl = chatConf?.chatBaseUrl + this._wssUrlChunk;
      this._stompClient = new Client({
        // Which server?
        webSocketFactory: this.socketProvider.bind(this),

        // Headers
        // Typical keys: login, passcode, host
        connectHeaders: {},

        // How often to heartbeat?
        // Interval in milliseconds, set to 0 to disable
        heartbeatIncoming: 0, // Typical value 0 - disabled
        heartbeatOutgoing: 0, // Typical value 0 - disabled
        // Wait in milliseconds before attempting auto reconnect
        // Set to 0 to disable
        // Typical value 1000 (1 seconds)
        reconnectDelay: 1000,

        // Will log diagnostics on console
        debug: str => {
          this.logger.debug(`[chatSocketService] ${new Date().toLocaleString()} ${str}`);
        },
      });

      this.contactFacade.created$
        .pipe(
          filter(({ booking }) => booking.channel.type === ChannelType.CHAT),
          takeUntil(this.destroy$)
        )
        .subscribe(({ booking, current }) => this.manageChatIncomingAccepting(booking, current));

      this.contactFacade.success$
        .pipe(
          filter(
            ({ booking, previous, current }) =>
              booking.channel.type === ChannelType.CHAT &&
              ((previous === BookingStatus.ACCEPTING && current === BookingStatus.ALLOCATED) || current === BookingStatus.IN_PROCESS)
          ),
          takeUntil(this.destroy$)
        )
        .subscribe(({ booking, current }) => this.manageChatIncomingAcceptation(booking, current));

      this.contactFacade.success$
        .pipe(
          filter(
            ({ booking, previous, current }) =>
              booking.channel.type === ChannelType.CHAT && previous === BookingStatus.ACCEPTING && current === BookingStatus.CLOSED
          ),
          takeUntil(this.destroy$)
        )
        .subscribe(({ booking }) => this.chatContactBuilderService.closeContact(booking.connectionId));

      this.contactFacade.success$.pipe(takeUntil(this.destroy$)).subscribe(({ booking, current }) => {
        if (booking.channel.type === ChannelType.CHAT && [BookingStatus.POST_PROCESSING, BookingStatus.CLOSED].indexOf(current) > -1) {
          const responseId = booking.responseId;
          const chatModel = this.getChatByResponseId(responseId);
          if (chatModel) {
            if (current === BookingStatus.POST_PROCESSING) {
              if (!chatModel.inPostTreatmentMode) {
                chatModel.inPostTreatmentMode = true;
                this.updateChatInQualificationCount();
              }
              this.resetTimer(chatModel);
            } else if (current === BookingStatus.CLOSED) {
              this.removeChat(chatModel.responseId);
            }
          }
        }
      });

      this.contactFacade.selected$.pipe(takeUntil(this.destroy$)).subscribe(contact => {
        this._selectedContact = contact;
      });

      this.chatContactBuilderService.newChatContact$
        .pipe(takeUntil(this.destroy$))
        .subscribe(({ chat, systemMessages }: { chat: ChatModel; systemMessages: ChatSystemMessage[] }) =>
          this.addChat(chat, systemMessages)
        );

      this.profileFacade.profile$.pipe(takeUntil(this.destroy$)).subscribe(profile => (this._profile = profile));
      this.agentConfigFacade.config$.pipe(takeUntil(this.destroy$)).subscribe(config => (this._agentConfig = config));
      this.agentConfigFacade
        .getAcceptation(ChannelUtil.INCOMING_CHAT)
        .pipe(isNotNullOrUndefined(), takeUntil(this.destroy$))
        .subscribe(acceptation => (this._acceptation = acceptation));

      // connect socket if agent has chat skills
      this.store$
        .pipe(
          waitToLoad([isAgentReady]),
          withLatestFrom(this.agentSkillFacade.getAll(ChannelUtil.INCOMING_CHAT), (_, skills) => skills.length > 0),
          filter(hasChatPermission => hasChatPermission),
          takeUntil(this.destroy$)
        )
        .subscribe(() => this.connect());

      this.actions$
        .pipe(
          ofType(CustomerAction.ReplaceSuccess),
          switchMap(({ data: { customer } }) =>
            of({}).pipe(
              withLatestFrom(this.customerFacade.getById(customer.id as string), (_, _customer) => _customer),
              isNotNullOrUndefined(),
              takeUntil(this.destroy$)
            )
          )
        )
        .subscribe(customer => {
          const chatModel =
            this.activeChat && this.activeChat.customerCardId === customer.id
              ? this.activeChat
              : this._chatList.find(chat => chat.customerCardId === customer.id);
          if (chatModel) {
            this.updateChatInfo(chatModel, customer);
          }
        });
    });

    this.actions$.pipe(ofType(BookingAction.LinkSuccess), takeUntil(this.destroy$)).subscribe(({ id, customerId }) => {
      const chatModel =
        this.activeChat && this.activeChat.responseId === id ? this.activeChat : this._chatList.find(chat => chat.responseId === id);
      if (chatModel) {
        this.customerFacade.getById(customerId).subscribe(customer => {
          if (customer) {
            chatModel.customerCardId = customerId;
            this.updateChatInfo(chatModel, customer);
          }
        });
      }
    });
  }

  destroy() {
    this._destroy$.next();
    this.disconnect();
  }

  private updateChatInfo(chatModel: ChatModel, customer: Customer) {
    const chatSystemMessage = new ChatSystemMessage(
      SystemMessageType.UPDATE_USER_INFO,
      chatModel.customerId,
      this._profile.id,
      this._profile.nickname,
      customer.firstname,
      customer.lastname,
      '',
      undefined,
      undefined,
      undefined,
      undefined,
      undefined
    );

    this.logger.debug(`[chatSocketService] ${new Date().toLocaleString()} updateChatInfo : tryToSendMessage`);
    try {
      this.sendSystemMessage(SYSTEM_MESSAGE_ENDPOINT, chatSystemMessage);
      let newChatName = ChatTab.computeChatName(customer.firstname, customer.lastname);
      this.updateChatName(chatModel, newChatName);
    } catch (e) {
      this.logger.warn(`[chatSocketService] ${new Date().toLocaleString()} error on updateChatInfo : ${e}`);
    }
  }

  private manageChatIncomingAccepting(contact: Booking, status: BookingStatus) {
    let toastContent = '';
    if (this._acceptation) {
      if (!!contact.forward && !!contact.forward.agentName) {
        if (contact.conference) {
          toastContent = this.translateService.instant(
            BookingStatus.ACCEPTING ? 'agent:toast.chat_conference_by' : 'agent:toast.join_chat_conference_by',
            { name: this.translateService.instant(contact.forward.agentName) }
          );
        } else {
          toastContent = this.translateService.instant(
            BookingStatus.ACCEPTING ? 'agent:toast.chat_transfer_by' : 'agent:toast.chat_conversation_transferred_by',
            { name: this.translateService.instant(contact.forward.agentName) }
          );
        }
      } else {
        const i18nKey = BookingStatus.ACCEPTING
          ? contact.lastBotName
            ? 'agent:toast.new_chat_conversation_from_bot'
            : 'agent:toast.new_chat_conversation'
          : contact.lastBotName
          ? 'agent:toast.chat_conversation_transferred'
          : 'agent:toast.chat_conversation_added';
        let option =
          contact.customerName && contact.customerName.trim() && contact.customerName.trim() !== ''
            ? contact.customerName
            : `agent:contact.tab.unknown_contact`;
        toastContent = this.translateService.instant(i18nKey, { name: this.translateService.instant(option) });
      }
      if (status === BookingStatus.ALLOCATED) {
        this.toasterFacade.notification({
          message: toastContent,
          cssClass: ToastClass.CHAT,
          duration: 5000,
        });
      }
    }
  }

  private manageChatIncomingAcceptation(contact: Booking, status: BookingStatus) {
    if (status === BookingStatus.ALLOCATED && this._acceptation) {
      let toastContent;
      if (!!contact.forward && !!contact.forward.agentName) {
        if (contact.conference) {
          toastContent = this.translateService.instant('agent:toast.join_chat_conference_by', {
            name: this.translateService.instant(contact.forward.agentName),
          });
        } else {
          toastContent = this.translateService.instant('agent:toast.chat_conversation_transferred_by', {
            name: this.translateService.instant(contact.forward.agentName),
          });
        }
      } else if (contact.lastBotName) {
        toastContent = this.translateService.instant('agent:toast.chat_conversation_transferred');
      } else {
        toastContent = this.translateService.instant('agent:toast.chat_conversation_added', {
          name: this.translateService.instant(contact.customerName),
        });
      }
      this.toasterFacade.notification({
        message: toastContent,
        cssClass: ToastClass.CHAT,
        duration: 5000,
      });
    } else {
      this.selectChatContact(contact, this._selectedContact);
    }
  }

  manageRefusedTransfer(responseId: string) {
    let chatModel = this.getChatByResponseId(responseId);
    if (chatModel) {
      if (chatModel.inTransferMode) {
        this.toasterFacade.notification({
          message: this.translateService.instant('agent:toast.chat_refused_transfer', { name: chatModel.conferenceCsrName }),
          cssClass: ToastClass.CHAT,
          duration: 5000,
        });
      } else {
        this.toasterFacade.notification({
          message: this.translateService.instant('agent:toast.chat_refused_conference', { name: chatModel.conferenceCsrName }),
          cssClass: ToastClass.CHAT,
          duration: 5000,
        });
      }
    }
  }

  manageLeaveConference(responseId: string) {
    let chatModel = this.getChatByResponseId(responseId);
    if (chatModel) {
      if (chatModel && chatModel.conference) {
        this.toasterFacade.notification({
          message: this.translateService.instant('agent:toast.leave_chat_conference', { name: chatModel.conferenceCsrName }),
          cssClass: ToastClass.CHAT,
          duration: 5000,
        });
        chatModel.conference = false;
        chatModel.conferenceCsrName = '';
        chatModel.inConferenceMode = false;
        // TODO CHAT FCA update booking front model but what's happening on F5 ?
        // I think booking will restore with previous value
        // This code need certainly a refactor
        // Update service response by http and let BL send a cometd to update front object
        this.contactFacade.update(responseId, {
          conference: false,
        });
      }
    }
  }

  private selectChatContact(chatContact: Booking, previousContact?: Booking) {
    if (!this.activeChat && chatContact.responseId !== this._selectedContact?.responseId) {
      this.contactFacade.select(chatContact.responseId);
      return;
    }
    if (!this._agentConfig || (this._agentConfig.openingChannels.length === 0 && chatContact.channel.type === ChannelType.CHAT)) {
      if (this.checkContactChatChannelType(previousContact) && chatContact.responseId === this._selectedContact?.responseId) {
        this.contactFacade.select(chatContact.responseId);
      }
    } else {
      this._agentConfig.openingChannels.forEach((mChannel: OpeningChannel) => {
        if (
          mChannel.channel.type === ChannelType.CHAT &&
          (!previousContact || ChannelUtil.isOpeningWith(mChannel, previousContact.channel))
        ) {
          if (this.checkContactChatChannelType(previousContact) && chatContact.responseId === this._selectedContact?.responseId) {
            this.contactFacade.select(chatContact.responseId);
          }
        }
      });
    }
  }

  private checkContactChatChannelType(previousContact?: Booking): boolean {
    if (!previousContact) {
      return true;
    } else if (previousContact.channel.type !== ChannelType.CHAT) {
      return true;
    }
    return false;
  }

  /**
   * List of chat conversations currently linked to the agent
   */
  get chatList(): ChatModel[] {
    return this._chatList;
  }

  /**
   * selected chat
   */
  get activeChat(): ChatModel | undefined {
    return !this._selectedContact ? undefined : this._chatMap.get(this._selectedContact.responseId);
  }

  /**
   * Open a stomp connection to the server and subscribe to the necessary topics
   * @return {angular.IPromise<{}>}
   */
  connect() {
    if (this._connected) {
      throw new Error('The chat stomp connection has already been opened');
    }

    this._headers = { userId: this._profile.id, selector: "to = '" + this._profile.id + "'" };
    this._stompClient.connectHeaders = this._headers;
    this._stompClient.onConnect = () => {
      this._connected = true;
      if (this.websocketLost) {
        this.toasterFacade.notification({
          message: this.translateService.instant('agent:panel.chat.server.server_retrieved'),
          duration: 5000,
        });
        this.websocketLost = false;
        this.removeAll();
      }
      this._subscriptions.push(this.subscribe(ERROR_MESSAGE_MAPPING, this.onGotChatErrorMessage.bind(this), this._headers));
      // copy the headers value into new variable
      let subscribeHeaders = JSON.parse(JSON.stringify(this._headers));
      subscribeHeaders['fromAgent'] = true;
      this._subscriptions.push(this.subscribe(INIT_DATA_MAPPING, this.onGotInitData.bind(this), subscribeHeaders));
      this._subscriptions.push(this.subscribe(MESSAGE_MAPPING, this.onGotChatMessage.bind(this), this._headers));
      this._subscriptions.push(this.subscribe(SYSTEM_MAPPING, this.onGotSystemMessage.bind(this), this._headers));
    };
    this._stompClient.onWebSocketClose = () => {
      this._connected = false;
      if (this._stompClient) {
        this._subscriptions.filter(notNullOrUndefined).forEach(subscription => {
          try {
            this._stompClient.unsubscribe(subscription.id);
          } catch (e) {
            this.logger.warn(`[chatSocketService] Subscribe ${subscription.id} already closed : ${e}`);
          }
        });
      }
      this._subscriptions = [];

      if (this._chatMap.size > 0 && !this.websocketLost) {
        this.toasterFacade.alert({
          message: this.translateService.instant('agent:panel.chat.server.server_lost'),
          duration: 5000,
        });
        this.websocketLost = true;
      }

      this._chatMap.forEach(chat => {
        if (!chat.tab.closedByClient) {
          let message: ChatMessage = new ChatMessage(MessageType.LEAVE, '', '', '', '', chat.customerId, this._profile.id, '', '');
          this.addMessage(message);
        }
        this.chatActions.hangUp(chat.responseId);
        this.removeChat(chat.responseId);
      });
    };
    this._stompClient.activate();
  }

  /**
   * Send the given message on the given stomp endpoint, if the connection is opened
   * @param {string} endpoint path to the stomp endpoint
   * @param {string} msg message to be send to the server
   */
  send(endpoint: string, msg: string): ChatMessage | undefined {
    if (this._connected && this.activeChat) {
      const chatMessage = new ChatMessage(
        MessageType.CHAT,
        msg,
        this._profile.lastname,
        this._profile.firstname,
        '',
        this.activeChat.customerId,
        this._profile.id,
        this._profile.login,
        this._profile.nickname
      );

      this._stompClient.publish({
        destination: endpoint,
        body: JSON.stringify(chatMessage),
        headers: this._headers,
      });
      this.startTimeoutVisitor(this.activeChat);
      this.resetTimer(this.activeChat);
      this.activeChat.addMessage(chatMessage);
      this.owiInstantMessages(chatMessage, this.activeChat);
      return chatMessage;
    }
    return undefined;
  }

  /**
   * Send system message on the given stomp endpoint, if the connection is opened
   * @param {string} endpoint path to the stomp endpoint
   * @param {SystemMessageType} type
   * @param {string} messageContent
   * @param {boolean} consent
   */
  createAndSendSystemMessage(endpoint: string, type?: SystemMessageType, messageContent?: string, consent?: boolean) {
    if (this._connected && this.activeChat) {
      const chatSystemMessage = new ChatSystemMessage(
        type,
        this.activeChat.customerId,
        this._profile.id,
        this._profile.nickname,
        this._profile.firstname,
        this._profile.lastname,
        messageContent,
        undefined,
        undefined,
        undefined,
        consent,
        undefined
      );
      this.sendSystemMessage(endpoint, chatSystemMessage);
    }
  }

  sendSystemMessage(endpoint: string, chatSystemMessage: ChatSystemMessage): void {
    this._stompClient.publish({
      destination: endpoint,
      body: JSON.stringify(chatSystemMessage),
      headers: this._headers,
    });
  }

  /**
   * Send system message on the given stomp endpoint, if the connection is opened
   * @param {string} endpoint path to the stomp endpoint
   * @param {SystemMessageType} type
   * @param {string} inputText
   */
  sendSystemMessageToAgent(endpoint: string, type?: SystemMessageType, inputText?: string) {
    if (this._connected && this.activeChat) {
      const chatSystemMessage = new ChatSystemMessage(
        type,
        this.activeChat.customerId,
        this._profile.id,
        this._profile.nickname,
        this._profile.firstname,
        this._profile.lastname,
        inputText,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined
      );
      this._stompClient.publish({
        destination: endpoint,
        body: JSON.stringify(chatSystemMessage),
        headers: this._headers,
      });
    }
  }

  /**
   * Send join message, if the connection is opened
   * @param {string} endpoint
   * @param {string} customerId
   * @param {MessageType} messageType
   * @param {string} oldCsrName name of the previous agent
   */
  sendJoinMessage(endpoint: string, customerId: string, messageType: MessageType, oldCsrName: string) {
    if (this._connected) {
      const chatMessage = new ChatMessage(
        messageType,
        oldCsrName,
        this._profile.lastname,
        this._profile.firstname,
        '',
        customerId,
        this._profile.id,
        this._profile.login,
        this._profile.nickname
      );
      this._stompClient.publish({
        destination: endpoint,
        body: JSON.stringify(chatMessage),
        headers: this._headers,
      });
    }
  }

  /**
   * Send survey message
   *
   * @param surveyUuid the survey uuid
   * @param surveyCustomerId the survey customer id
   */
  sendSurveyMessage(surveyUuid: string, surveyCustomerId: string) {
    if (this._connected && this.activeChat) {
      const chatMessage = new ChatMessage(
        MessageType.SURVEY,
        JSON.stringify({ surveyUuid: surveyUuid, surveyCustomerId: surveyCustomerId }),
        this._profile.lastname,
        this._profile.firstname,
        '',
        this.activeChat.customerId,
        this._profile.id,
        this._profile.login,
        this._profile.nickname
      );
      this._stompClient.publish({
        destination: MESSAGE_ENDPOINT,
        body: JSON.stringify(chatMessage),
        headers: this._headers,
      });
    }
  }

  /**
   * Send comment transfer message
   * @param {string} comment
   */
  sendTransferCommentMessage(comment: string) {
    if (this._connected && this.activeChat) {
      const chatMessage = new ChatMessage(
        MessageType.TRANSFER_COMMENT,
        comment,
        this._profile.lastname,
        this._profile.firstname,
        '',
        this.activeChat.customerId,
        this._profile.id,
        this._profile.login,
        this._profile.nickname
      );
      this._stompClient.publish({
        destination: MESSAGE_ENDPOINT,
        body: JSON.stringify(chatMessage),
        headers: this._headers,
      });
    }
  }

  sendScriptRedirectionMessage() {
    if (this._connected && this.activeChat) {
      const chatMessage = new ChatMessage(
        MessageType.SCRIPT_REDIRECTION,
        '',
        this._profile.lastname,
        this._profile.firstname,
        '',
        this.activeChat.customerId,
        this._profile.id,
        this._profile.login,
        this._profile.nickname
      );
      this._stompClient.publish({
        destination: MESSAGE_ENDPOINT,
        body: JSON.stringify(chatMessage),
        headers: this._headers,
      });
    }
  }

  sendScreenSharingDemandMessage(customerId: string) {
    if (this._connected && this.activeChat) {
      const chatMessage = new ChatMessage(
        MessageType.DEMAND_SCREEN_SHARING,
        '',
        this._profile.lastname,
        this._profile.firstname,
        '',
        customerId,
        this._profile.id,
        this._profile.login,
        this._profile.nickname
      );
      this._stompClient.publish({
        destination: MESSAGE_ENDPOINT,
        body: JSON.stringify(chatMessage),
        headers: this._headers,
      });
      this.activeChat.addMessage(chatMessage);
    }
  }

  sendStopScreenSharingMessage(customerId: string) {
    if (this._connected && this.activeChat) {
      const chatMessage = new ChatMessage(
        MessageType.END_SCREEN_SHARING,
        '',
        this._profile.lastname,
        this._profile.firstname,
        '',
        customerId,
        this._profile.id,
        this._profile.login,
        this._profile.nickname
      );
      this._stompClient.publish({
        destination: MESSAGE_ENDPOINT,
        body: JSON.stringify(chatMessage),
        headers: this._headers,
      });
      this.activeChat.addMessage(chatMessage);
    }
  }

  /**
   * Send file message, if the connection is opened
   * @param {string} endpoint
   * @param {string} fileDownloadUri
   * @param {string} fileName
   * @param {number} size
   */
  sendFileMessage(endpoint: string, fileDownloadUri: string, fileName: string, size: number): void {
    if (this._connected && this.activeChat) {
      const chatMessage = new ChatMessage(
        MessageType.FILE,
        fileName,
        this._profile.lastname,
        this._profile.firstname,
        '',
        this.activeChat.customerId,
        this._profile.id,
        this._profile.login,
        this._profile.nickname,
        0,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        new FileResponse(fileDownloadUri, fileName, size)
      );

      this._stompClient.publish({
        destination: endpoint,
        body: JSON.stringify(chatMessage),
        headers: this._headers,
      });
      this.activeChat.addMessage(chatMessage);
    }
  }

  private updateConsentIfNeeded(responseId: any, consent: boolean) {
    this.contactFacade.getById(responseId).subscribe(contact => {
      if (contact && contact.customerId) {
        this.customerService.load(contact.customerId).subscribe(customerCard => {
          let { customer: firstCustomerCard } = customerCard;
          if (firstCustomerCard) {
            if (!firstCustomerCard.consent && consent) {
              this.customerFacade.update(firstCustomerCard.id, {
                consent: consent ?? false,
              });
            }
            if (firstCustomerCard.consent) {
              this.createAndSendSystemMessage(SYSTEM_MESSAGE_ENDPOINT, SystemMessageType.CONSENT, '', firstCustomerCard.consent);
            }
          }
        });
      }
    });
  }

  /**
   * Disconnect from the stomp server
   * @return {angular.IPromise<{}>}
   */
  disconnect() {
    this._stompClient.deactivate();
  }

  /**
   * Make an available chat selected
   * @param {ChatModel} chat the chat to select
   */
  selectChat(chat: ChatModel) {
    if (chat.responseId !== this._selectedContact?.responseId) {
      this.contactFacade.select(chat.responseId);
    }
    chat.tab.resetUnreadCount();
    this.updateChatNewMessageCount();
  }

  /**
   * Add a chat conversation into the active chat list
   * @param chatModel the chat model to add
   * @param systemMessages a list of system messages to play once the chat is added
   */
  addChat(chatModel: ChatModel, systemMessages: ChatSystemMessage[]) {
    if (!chatModel || !chatModel.responseId) {
      return;
    }
    const existingChat = this._chatMap.get(chatModel.responseId);
    if (existingChat) {
      return;
    }
    const { customerId, messages, chatSlaConf, responseId } = chatModel;
    const message = messages.find(mes => mes.isFromRealAgent());
    this._chatMap.set(responseId, chatModel);
    this._chatList.push(chatModel);
    if (this._chatList.length === 1 && chatModel.responseId === this._selectedContact?.responseId) {
      this.selectChat(chatModel);
    }

    this.updateChatTabStatus();
    this.chatTabService.firstMessageTime = chatModel.tab.firstMessageTime;
    this.chatTabService.lastMessageDate = new Date(chatModel.tab.lastMessageTime);

    // Update the consent of the customer if the first message contains the response of the customer about the GDPR
    if (messages.length > 0) {
      this.updateConsentIfNeeded(chatModel.responseId, messages[0].consent || false);
    }

    // Get the date of the last message (first message in the last group message) if the message is come from the customer. The date will be use to add time of the SLA timer and last message timer.
    const oldestUnansweredMessageFromCustomer = chatModel.getOldestUnansweredMessageFromCustomer();
    let beginDuration: number = oldestUnansweredMessageFromCustomer ? Date.now() - oldestUnansweredMessageFromCustomer.date : 0;
    this.chatTimerService.startTimerForLastMessage(chatModel, beginDuration);

    if (chatSlaConf) {
      if (beginDuration > chatSlaConf.slaDuration * 1000) {
        this.chatTimerService.startSlaTimer(chatModel, beginDuration - chatSlaConf.slaDuration * 1000);
      } else {
        this.chatTimerService.startDurationExceededCountdownTimer(chatModel, chatSlaConf.slaDuration * 1000 - beginDuration);
      }
    }

    if (chatModel.transferred && chatModel.conferenceCsrName) {
      let requestingCsrName = chatModel.conferenceCsrName;
      if (chatModel.requestingCsrNickname) {
        requestingCsrName = chatModel.requestingCsrNickname;
      }
      if (chatModel.conference) {
        this.sendJoinMessage(MESSAGE_ENDPOINT, customerId, MessageType.CONFERENCE, requestingCsrName);
        if (message) {
          this.agentImageService.loadImage(message.csrId);
        }
      } else {
        this.sendJoinMessage(MESSAGE_ENDPOINT, customerId, MessageType.TRANSFER, requestingCsrName);
        if (message) {
          this.agentImageService.loadImage(message.csrId);
        }
      }
    } else {
      this.sendJoinMessage(MESSAGE_ENDPOINT, customerId, MessageType.JOIN, '');
    }

    if (this.isWelcomeMessageActive) {
      setTimeout(() => {
        this.contactFacade.bookings$.pipe().subscribe(contacts => {
          const contactLanguage =
            contacts.find(contact => contact.connectionId === chatModel.connectionId)?.context['CHAT_LANGUAGE'] ?? 'en';

          this.translateService.loadLanguages([contactLanguage]).then(() => {
            const isChatModelNew = !chatModel.messages.some(
              chatMessage =>
              chatMessage.isFromRealAgent() &&
              chatMessage.csrId === this._profile.id &&
              chatMessage.type === MessageType.CHAT_AUTOMATIC_MESSAGE
            );

            if (
              this._connected &&
              isChatModelNew &&
              !(chatModel.transferred && !this.messageDuringTransfer) &&
              !chatModel.inConferenceMode &&
              this.welcomeMessage !== ''
            ) {
              const chatMessage = new ChatMessage(
                MessageType.CHAT_AUTOMATIC_MESSAGE,
                this.translateService.instant(this.welcomeMessage, { agent: this._profile.firstname, lng: contactLanguage }),
                this._profile.lastname,
                this._profile.firstname,
                '',
                customerId,
                this._profile.id,
                this._profile.login,
                this._profile.nickname
              );

              this._stompClient.publish({
                destination: MESSAGE_ENDPOINT,
                body: JSON.stringify(chatMessage),
                headers: this._headers,
              });
              chatModel.addMessage(chatMessage);
            }
            systemMessages.forEach(systemMessage => {
              this.addSystemMessage(systemMessage);
            });
          });
        });
      }, 2000);
    }

    if (chatModel.customerCardId) {
      this.customerFacade.getById(chatModel.customerCardId).subscribe(customer => {
        if (customer) {
          this.updateChatInfo(chatModel, customer);
        }
      });
    }
  }

  /**
   * Get chatModel from responseId
   * @param responseId
   * @return ChatModel or null
   */
  getChatByResponseId(responseId: string): ChatModel | undefined {
    if (responseId) {
      return this._chatMap.get(responseId);
    }
    return undefined;
  }

  /**
   * @inheritDoc
   */
  private getChatByContactId(contactId: string): ChatModel | undefined {
    if (contactId) {
      return this._chatList.find(c => c.connectionId === contactId);
    }
    return undefined;
  }

  /**
   * Get date of the first message
   * @param responseId
   * @return number or null
   */
  getFirstMessageByVisitorByResponseId(responseId: string): number | undefined {
    if (responseId) {
      let chatList = this._chatList.find(c => c.responseId === responseId);
      if (chatList) {
        let messages = chatList.messages.find(c => c.type === MessageType.CHAT && c.isFromCustomer());
        if (messages && messages.date) {
          return messages.date;
        }
      }
    }
    return undefined;
  }

  /**
   * @inheritDoc
   */
  removeAll(): void {
    this._chatMap.clear();
    this._chatList = [];
  }

  /**
   * End the chat for the given customer
   * @param {string} responseId id of the response
   * @return {boolean} true if it succeeds, false otherwise
   */
  private removeChat(responseId: string): boolean {
    const chat = this._chatMap.get(responseId);
    if (chat) {
      this._chatMap.delete(responseId);
      const chatIndex = this._chatList.indexOf(chat);
      this._chatList.splice(chatIndex, 1);
      this._selectedContact = undefined;
      this.chatContactBuilderService.closeContact(chat.connectionId);

      if (this._chatList.length > 0) {
        this.selectChat(this._chatList[0]);
      }

      this.updateChatTabStatus();
      this.chatTabService.computeLastMessageDateForAllChats(this._chatList);
      this.updateChatInQualificationCount();
      this.resetTimer(chat);

      return true;
    }
    return false;
  }

  /**
   * Update number of chat which is in PostTreatment
   */
  updateChatInQualificationCount(): void {
    this._chatInQualificationCount$.next(this.chatList.filter(chat => chat.inPostTreatmentMode).length);
  }

  /**
   * Update number of chat who is in PostTreatmentDurationExceeded
   */
  updateChatHavePostTreatmentDurationExceeded(): void {
    this._chatHavePostTreatmentDurationExceeded$.next(this.chatList.some(chat => chat.inPostTreatmentExceededMode));
  }

  /**
   * subscribe to a specific topic of the stomp server
   * @param {string} destination topic identifier
   * @param {Function} callback
   * @param {StompHeaders} headers
   * @return {StompSubscription}
   */
  protected subscribe(destination: string, callback: messageCallbackType, headers: StompHeaders): StompSubscription | undefined {
    if (this._connected) {
      return this._stompClient.subscribe(destination, callback, headers);
    }
    return undefined;
  }

  /**
   * Update the chat status bar (chat count / chat name)
   */
  protected updateChatTabStatus(): void {
    this.chatTabService.conversationCount = this._chatList.length;
    if (this._chatList.length === 1 && this._chatList[0].tab.chatName) {
      this.chatTabService.chatName = this._chatList[0].tab.chatName;
    }
  }

  /**
   * Get number of new message count on the channel tab
   */

  updateChatNewMessageCount(): void {
    this._chatNewMessageCount$.next(this._chatList.reduce((prev, curr) => prev + curr.tab.newMessagesCount, 0));
  }

  /**
   * Add a message received from the stomp topic to the good chat conversation
   * @param {ChatMessage} message
   */
  protected addMessage(message: ChatMessage): void {
    let chat = this.getChatByContactId(message.contactId || '');
    if (!chat) {
      if (message.type === MessageType.JOIN) {
        this.chatContactBuilderService.addChatMessage(message);
      }
    } else {
      this.handleChatBannerTab(message, chat);
      let lastMessageFromCustomer = chat.messages[chat.messages.length - 1].isFromCustomer();
      this.removeLeaveMessage(message.customerId || '');
      chat.addMessage(message);
      this.owiInstantMessages(message, chat);
      if (message.isFromCustomer()) {
        if (!lastMessageFromCustomer) {
          if (chat.chatSlaConf) {
            this.chatTimerService.startDurationExceededCountdownTimer(chat, chat.chatSlaConf.slaDuration * 1000);
            if (chat.tab.slaTimer !== -1) {
              this.chatTimerService.stopSlaTimer(chat);
            }
          }
          this.resetTimeoutVisitor(chat);
          this.chatTimerService.startTimerForLastMessage(chat);
        }

        let newChatName = ChatTab.computeChatNameFromMessage(message);
        if (newChatName && newChatName !== chat.tab.chatName) {
          chat.tab.chatName = newChatName;
          this.updateChatTabStatus();
        }
        this.chatTabService.lastMessageDate = new Date(message.date);
        if (chat !== this.activeChat) {
          chat.increaseUnreadCount();
          this.updateChatNewMessageCount();
        }
        this.removeTypingCustomerMessage(message.customerId || '');
      } else if (!chat.tab.isLastMessageFromClient) {
        this.startTimeoutVisitor(chat);
        chat.resetUnreadCount();
        this.updateChatNewMessageCount();
        this.removeTypingAgentMessage(message.csrId);
        this.resetTimer(chat);
      }

      this.applyTreatmentForNewMessage(message, chat);
    }
  }

  handleChatBannerTab(message: ChatMessage, chat: ChatModel) {
    switch (message.type) {
      case MessageType.LEAVE:
        chat.tab.conversationEndByClient = false;
        break;
      case MessageType.END:
        chat.tab.closedByClient = false;
        break;
      default:
        break;
    }
  }

  private applyTreatmentForNewMessage(message: ChatMessage, chat: ChatModel) {
    switch (message.type) {
      case MessageType.DEMAND_SCREEN_SHARING:
        this.screenSharingStatusService.updateScreenSharingStatus({
          chat,
          info: new ScreenSharingInfo(ScreenSharingStatus.PENDING_INVITATION),
        });
        break;
      case MessageType.REFUSE_SCREEN_SHARING:
      case MessageType.CANCEL_SCREEN_SHARING:
        chat.removeMessagesOfType(MessageType.DEMAND_SCREEN_SHARING);
        this.screenSharingStatusService.updateScreenSharingStatus({
          chat,
          info: new ScreenSharingInfo(ScreenSharingStatus.NOT_STARTED),
        });
        break;
      case MessageType.END_SCREEN_SHARING:
      case MessageType.FAILURE_SCREEN_SHARING:
        this.screenSharingStatusService.updateScreenSharingStatus({
          chat,
          info: new ScreenSharingInfo(ScreenSharingStatus.NOT_STARTED),
        });
        break;
      case MessageType.CONFERENCE:
      case MessageType.TRANSFER:
        this.agentImageService.loadImage(message.csrId);
        break;
      default:
        // No need for dedicated management, just go on
        break;
    }
  }

  private owiInstantMessages(msg: ChatMessage, chatModel: ChatModel) {
    // check right
    if (this.profileService.hasPermission('CSR::answer_models:display')) {
      if (msg.type === MessageType.CHAT) {
        let from;
        let to;
        if (msg.isFromCustomer()) {
          from = msg.customerId;
          to = this._profile.id;
        } else {
          from = msg.csrId;
          to = msg.customerId;
        }

        let owiInstant = new OwiInstantMessage(msg.contactId || '', msg.date.toString(), from, to, msg.content, 'CHAT');
        this.http
          .withHeaders({
            'Content-Type': 'application/json; charset=UTF-8',
            Accept: 'application/json',
          })
          .post(`${CSR_FACADE_PREFIX}/getOwiInstantMessage`, owiInstant)
          .subscribe(requestId => {
            if (requestId) {
              chatModel.owiSextId = requestId.toString();
              if (this._selectedContact) {
                const knowledgeBaseService = this.injector.get(NG1_KNOWLEDGE_BASE);
                knowledgeBaseService.loadUrl(this._selectedContact.channel.type, chatModel.owiSextId);
              }
            }
          });
      }
    }
  }

  private resetTimer(chat: ChatModel) {
    if (chat.tab.timerForLastMessage !== -1) {
      this.chatTimerService.stopTimerForLastMessage(chat);
    }
    if (chat.chatSlaConf) {
      if (chat.tab.slaTimer !== -1) {
        this.chatTimerService.stopSlaTimer(chat);
      }
      if (chat.tab.durationExceededCountdownTimer !== -1) {
        this.chatTimerService.stopDurationExceededCountdownTimer(chat);
      }
    }
  }

  /**
   * Add a system message received from the stomp topic to the good chat conversation
   * @param {ChatSystemMessage} systemMessage
   */
  protected addSystemMessage(systemMessage: ChatSystemMessage): void {
    let chat = this.getChatByContactId(systemMessage.contactId || '');
    if (!chat) {
      this.chatContactBuilderService.addChatSystemMessage(systemMessage);
      if (systemMessage.type == SystemMessageType.WEBSOCKET_DISCONNECT_ORDER) {
        this.disconnect();
      }
    } else {
      switch (systemMessage.type) {
        case SystemMessageType.TYPING: // conference typing logic
          if (systemMessage.isFromCustomer()) {
            let index = chat.typing.findIndex(t => t.isFromCustomer() && t.customerId === systemMessage.customerId);
            this.removeAndAddSystemMessage(index, systemMessage, chat);
            chat.typingCounterCustomer += 1;
            setTimeout(() => {
              this._chatMap.forEach(c => {
                if (c.typing) {
                  index = c.typing.findIndex(typing => typing.isFromCustomer() && typing.customerId === systemMessage.customerId);
                  if (index > -1 && c.typingCounterCustomer > 0) {
                    c.typingCounterCustomer -= 1;
                  }
                }
              });
            }, 10000);
          } else {
            let index = chat.typing.findIndex(t => !t.isFromCustomer() && t.csrId === systemMessage.csrId);
            chat.typingCounterAgent += 1;
            this.removeAndAddSystemMessage(index, systemMessage, chat);
            setTimeout(() => {
              this._chatMap.forEach(c => {
                if (c.typing) {
                  index = c.typing.findIndex(typing => !typing.isFromCustomer() && typing.csrId === systemMessage.csrId);
                  if (index > -1 && c.typingCounterAgent > 0) {
                    c.typingCounterAgent -= 1;
                  }
                }
              });
            }, 10000);
          }
          break;
        case SystemMessageType.TYPING_END:
          if (systemMessage.content !== undefined && systemMessage.content === '') {
            if (systemMessage.isFromCustomer()) {
              this.removeTypingCustomerMessage(systemMessage.customerId || '');
            } else {
              this.removeTypingAgentMessage(systemMessage.csrId);
            }
          }
          break;
        case SystemMessageType.VISITOR_STATS:
          this.chatStatsService.setStatsMessage(chat.responseId, systemMessage.stats);
          const messageIndex = chat.systemMessages.findIndex(msg => msg.type === SystemMessageType.VISITOR_STATS);
          if (messageIndex > -1) {
            chat.systemMessages[messageIndex] = systemMessage;
          } else {
            chat.systemMessages.push(systemMessage);
          }
          break;
        case SystemMessageType.START_SCREEN_SHARING:
          const params = systemMessage.stats;
          this.screenSharingStatusService.updateScreenSharingStatus({
            chat,
            info: new ScreenSharingInfo(ScreenSharingStatus.ONGOING, params['apiKey'], params['sessionId'], params['token']),
          });
          break;
        case SystemMessageType.NO_CAPABILITY_FOR_SCREEN_SHARING:
          chat.checkCapabilityForScreenSharing = false;
          break;
        case SystemMessageType.UPDATE_USER_INFO:
          let newChatName = ChatTab.computeChatName(systemMessage.firstName, systemMessage.lastName);
          this.updateChatName(chat, newChatName);
          break;
        case SystemMessageType.WEBSOCKET_DISCONNECT_ORDER:
          this.disconnect();
          break;
        default:
          break;
      }
    }
  }

  /**
   * Handler for messages received on the error stomp topic
   * @param message
   */
  protected onGotChatErrorMessage(message: any): void {
    this.logger.error(message.body);
  }

  /**
   * Handler for messages received on the private messages stomp topic
   * @param json
   */
  protected onGotInitData(json: any): void {
    const initData = JSON.parse(json.body) as ChatInitData;
    initData.messages = initData.messages.map(ChatMessage.fromWebSocket);
    initData.systemMessages = initData.systemMessages.map(ChatSystemMessage.fromWebSocket);
    this.removeAll();
    this.chatContactBuilderService.setInitData(initData);
  }

  /**
   * Handler for messages received on the private messages stomp topic
   * @param message
   */
  protected onGotChatMessage(message: any): void {
    this.addMessage(ChatMessage.fromWebSocket(JSON.parse(message.body)));
  }

  /**
   * Handler for messages received on the system messages stomp topic
   * @param systemMessage
   */
  protected onGotSystemMessage(systemMessage: any): void {
    this.addSystemMessage(ChatSystemMessage.fromWebSocket(JSON.parse(systemMessage.body)));
  }

  private socketProvider(): SockJS {
    return new SockJS(this._chatUrl);
  }

  private removeTypingCustomerMessage(customerId: string | undefined) {
    if (customerId === undefined) {
      return;
    }
    this._chatMap.forEach(chat => {
      if (chat.typing) {
        const index = chat.typing.findIndex(typing => typing.isFromCustomer() && typing.customerId === customerId);
        if (index > -1) {
          chat.typing.splice(index, 1);
        }
      }
    });
  }

  private removeLeaveMessage(customerId: string) {
    if (!this.activeChat) {
      return;
    }
    if (this.activeChat.messages == null) {
      this.activeChat.messages = [];
    }
    const index = this.activeChat.messages.findIndex(mes => mes.type === MessageType.LEAVE && mes.customerId === customerId);
    if (index > -1) {
      this.activeChat.messages.splice(index, 1);
    }
  }

  private removeTypingAgentMessage(csrId: string) {
    this._chatMap.forEach(chat => {
      if (chat.typing) {
        const index = chat.typing.findIndex(typing => !typing.isFromCustomer() && typing.csrId === csrId);
        if (index > -1) {
          chat.typing.splice(index, 1);
        }
      }
    });
  }

  private removeAndAddSystemMessage(index: number, systemMessage: ChatSystemMessage, chat: ChatModel) {
    if (index > -1) {
      chat.typing.splice(index, 1);
      chat.typing.push(systemMessage);
    } else {
      chat.typing.push(systemMessage);
    }
  }

  private updateChatName(chat: ChatModel | undefined, newChatName) {
    if (chat && newChatName && newChatName !== chat.tab.chatName) {
      chat.tab.chatName = newChatName;
      this.updateChatTabStatus();
    }
  }

  startTimeoutVisitor(chat: ChatModel) {
    if (chat.inactiveTimer) {
      return;
    }
    chat.inactiveTimer = setTimeout(() => {
      chat.isCustomerInactive = true;
      setTimeout(() => {
        if (chat === this.activeChat) {
          let messagesElement = document.getElementsByClassName('chat-history').item(0);
          if (messagesElement) {
            messagesElement.scrollTop = messagesElement.scrollHeight;
          }
        }
      }, 500);
    }, 600000);
  }

  resetTimeoutVisitor(chat: ChatModel) {
    if (chat.inactiveTimer) {
      chat.isCustomerInactive = false;
      clearTimeout(chat.inactiveTimer);
      chat.inactiveTimer = null;
    }
  }
}
